import type { Branch, DailySale } from './domain';

const money = new Intl.NumberFormat('th-TH', { maximumFractionDigits: 2 });

export function thaiDisplayDate(date: string) {
  return new Intl.DateTimeFormat('th-TH', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Bangkok' }).format(new Date(`${date}T12:00:00+07:00`));
}

export interface BranchSlipItem {
  id: string;
  name: string;
  amount: number;
  checks: Array<{ text: string; ok: boolean }>;
}

export interface ReportParts {
  fullText: string;
  dateStr: string;
  branches: BranchSlipItem[];
  total: number;
  regionTotals: Array<[string, number]>;
}

export function buildReportParts(branches: Branch[], sales: DailySale[], date: string): ReportParts {
  const active = branches.filter((branch) => branch.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
  const byBranch = new Map(sales.filter((sale) => sale.salesDate === date).map((sale) => [sale.branchId, sale]));
  const regionTotals = new Map<string, number>();
  let total = 0;
  const dateStr = thaiDisplayDate(date);
  const headerLines = [`สรุปยอดประจำวันที่ ${dateStr}`, ''];
  const branchLines: string[] = [];
  const slipBranches: BranchSlipItem[] = [];

  active.forEach((branch, index) => {
    const sale = byBranch.get(branch.id);
    const amount = sale?.salesAmount ?? 0;
    total += amount;
    regionTotals.set(branch.region, (regionTotals.get(branch.region) ?? 0) + amount);
    const mark = (ok: boolean | undefined, yes: string, no: string) => `${ok ? '✅' : '❌'}${ok ? yes : no}`;

    const checks = [
      { text: sale?.paidComplete ? 'โอนครบ' : 'โอนไม่ครบ', ok: Boolean(sale?.paidComplete) },
      { text: sale?.posComplete ? 'POSครบ' : 'POSไม่ครบ', ok: Boolean(sale?.posComplete) },
      { text: sale?.billComplete ? 'บิลครบ' : 'บิลไม่ครบ', ok: Boolean(sale?.billComplete) },
      { text: sale?.stockAttached ? 'แนบสต๊อกแล้ว' : 'ยังไม่แนบสต๊อก', ok: Boolean(sale?.stockAttached) },
      { text: sale?.salesLinkUpdated ? 'ยอดขายลงในลิงค์แล้ว' : 'ยังไม่ลงยอดขายในลิงค์', ok: Boolean(sale?.salesLinkUpdated) },
      { text: sale?.dailyInspectionAttached ? 'แนบใบตรวจเช็คสาขารายวันแล้ว' : 'ยังไม่แนบใบตรวจเช็คสาขารายวัน', ok: Boolean(sale?.dailyInspectionAttached) },
      { text: sale?.unpluggedPhotoConfirmed ? 'แนบรูปถอดปลั๊กไฟเรียบร้อย' : 'ยังไม่แนบรูปถอดปลั๊กไฟ', ok: Boolean(sale?.unpluggedPhotoConfirmed) }
    ];

    slipBranches.push({
      id: branch.id,
      name: branch.name,
      amount,
      checks
    });

    branchLines.push(
      `${index + 1}. สาขา${branch.name}`,
      `💵 ยอดวันนี้ ${money.format(amount)} บาท`,
      mark(sale?.paidComplete, 'โอนครบ', 'โอนไม่ครบ'),
      mark(sale?.posComplete, 'POSครบ', 'POSไม่ครบ'),
      mark(sale?.billComplete, 'บิลครบ', 'บิลไม่ครบ'),
      mark(sale?.stockAttached, 'แนบสต๊อกแล้ว', 'ยังไม่แนบสต๊อก'),
      mark(sale?.salesLinkUpdated, 'ยอดขายลงในลิงค์แล้ว', 'ยังไม่ลงยอดขายในลิงค์'),
      mark(sale?.dailyInspectionAttached, 'แนบใบตรวจเช็คสาขารายวันแล้ว', 'ยังไม่แนบใบตรวจเช็คสาขารายวัน'),
      mark(sale?.unpluggedPhotoConfirmed, 'แนบรูปถอดปลั๊กไฟเรียบร้อย', 'ยังไม่แนบรูปถอดปลั๊กไฟ'),
      ''
    );
  });

  const zonePairs = [...regionTotals.entries()];
  const zoneLines = [
    `💷 ยอดขายรวม : ${money.format(total)} บาท`,
    '',
    '👑 สรุปรวมตามโซนภาค',
    ...zonePairs.map(([region, amount]) => `📍${region}: ${money.format(amount)} บาท`)
  ];

  const fullText = [...headerLines, ...branchLines, ...zoneLines].join('\n').trim();

  return {
    fullText,
    dateStr,
    branches: slipBranches,
    total,
    regionTotals: zonePairs
  };
}

export function buildReport(branches: Branch[], sales: DailySale[], date: string) {
  return buildReportParts(branches, sales, date).fullText;
}

export function totalsByRegion(sales: DailySale[]) {
  const totals = new Map<string, number>();
  for (const sale of sales) totals.set(sale.regionSnapshot, (totals.get(sale.regionSnapshot) ?? 0) + (sale.salesAmount ?? 0));
  return [...totals].sort((a, b) => b[1] - a[1]);
}
